import React from "react";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";

// The first thing a person meets on the shared-device task kiosk: who are
// you? Two shapes, chosen by the store's kiosk_task_pin_required flag.
//
//   list mode: a grid of names from the store roster; tap yours.
//   PIN mode:  a keypad for the time clock PIN; nothing on screen names
//              anyone, so a stranger at the iPad learns nothing.
//
// onIdentify(params) receives {store_team_member_id} or {clock_pin} and
// resolves to an error message (shown inline) or null on success. The server
// is the authority on both: in PIN mode a member id is refused outright.

// clock_pin has no minimum length (some stores use one to three digits, and
// the time clock keypad accepts them), so Enter unlocks on the first digit.
// Four dots are only the customary width of the display.
const PIN_MIN = 1;
const PIN_DOTS = 4;
const PIN_MAX = 6;

function initialsOf(member) {
  const name = displayName(member);
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function displayName(member) {
  return (member.full_name || `${member.first_name || ""} ${member.last_name || ""}`).trim();
}

function PinPad({ onIdentify, busy }) {
  const [pin, setPin] = React.useState("");
  const [error, setError] = React.useState(null);

  const push = (d) => {
    setError(null);
    setPin((p) => (p.length >= PIN_MAX ? p : p + d));
  };
  const pop = () => setPin((p) => p.slice(0, -1));
  const clear = () => {
    setPin("");
    setError(null);
  };
  const submit = async () => {
    if (busy || pin.length < PIN_MIN) return;
    const message = await onIdentify({ clock_pin: pin });
    if (message) {
      setError(message);
      setPin("");
    }
  };

  // Physical keyboards on a kiosk are rare, but a manager testing on a laptop
  // should not have to click twelve buttons.
  React.useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) push(e.key);
      else if (e.key === "Backspace") pop();
      else if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, busy]);

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

  return (
    <div className="flex flex-col items-center">
      <h3 className="text-lg font-semibold text-slate-900">Enter your PIN</h3>
      <p className="mt-1 text-sm text-slate-500">Your time clock PIN identifies you here.</p>
      <div className="mt-5 flex items-center gap-3" aria-label="PIN">
        {Array.from({ length: Math.max(PIN_DOTS, pin.length) }).map((_, i) => (
          <span
            key={i}
            className={`h-4 w-4 rounded-full border-2 ${
              i < pin.length ? "bg-violet-700 border-violet-700" : "border-slate-300"
            }`}
          />
        ))}
      </div>
      <div className="h-5 mt-3 text-sm font-medium text-rose-600">{error || ""}</div>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {keys.map((k) => {
          if (k === "clear") {
            return (
              <button
                key={k}
                type="button"
                onClick={clear}
                className="h-16 w-20 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-100"
              >
                Clear
              </button>
            );
          }
          if (k === "back") {
            return (
              <button
                key={k}
                type="button"
                onClick={pop}
                aria-label="Delete"
                className="h-16 w-20 rounded-xl text-slate-500 hover:bg-slate-100 flex items-center justify-center"
              >
                <BackspaceOutlinedIcon fontSize="small" />
              </button>
            );
          }
          return (
            <button
              key={k}
              type="button"
              onClick={() => push(k)}
              className="h-16 w-20 rounded-xl border border-slate-200 bg-white text-2xl font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100"
            >
              {k}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={busy || pin.length < PIN_MIN}
        className="mt-5 w-64 px-5 py-3 rounded-xl bg-violet-700 hover:bg-violet-800 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Enter
      </button>
    </div>
  );
}

function NameGrid({ roster, rosterError, onRetryRoster, onIdentify, busy }) {
  const [query, setQuery] = React.useState("");
  const [error, setError] = React.useState(null);
  const [pendingId, setPendingId] = React.useState(null);
  const people = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return (roster || [])
      .filter((m) => !q || displayName(m).toLowerCase().includes(q))
      .sort((a, b) => displayName(a).localeCompare(displayName(b)));
  }, [roster, query]);

  const pick = async (member) => {
    if (busy) return;
    setError(null);
    setPendingId(member.id);
    const message = await onIdentify({ store_team_member_id: member.id });
    setPendingId(null);
    if (message) setError(message);
  };

  return (
    <div className="flex flex-col items-center">
      <h3 className="text-lg font-semibold text-slate-900">Who's working?</h3>
      <p className="mt-1 text-sm text-slate-500">Tap your name to see your tasks.</p>
      {(roster || []).length > 12 && (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search names"
          className="mt-4 w-72 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
        />
      )}
      <div className="h-5 mt-3 text-sm font-medium text-rose-600">{error || ""}</div>
      {rosterError ? (
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="text-sm text-slate-500">The roster could not be loaded.</p>
          <button
            type="button"
            onClick={onRetryRoster}
            className="text-sm font-semibold text-violet-700 hover:text-violet-800"
          >
            Try again
          </button>
        </div>
      ) : roster === null ? (
        <p className="mt-2 text-sm text-slate-500">Loading the roster...</p>
      ) : people.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">
          {roster.length ? "No one matches that name." : "Nobody on the roster yet."}
        </p>
      ) : (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 w-full max-w-3xl">
          {people.map((m) => (
            <button
              key={m.id}
              type="button"
              disabled={busy}
              onClick={() => pick(m)}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl border bg-white text-left hover:border-violet-300 hover:bg-violet-50 disabled:opacity-60 ${
                pendingId === m.id ? "border-violet-400" : "border-slate-200"
              }`}
            >
              <span className="h-9 w-9 flex-none rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">
                {initialsOf(m)}
              </span>
              <span className="text-sm font-semibold text-slate-800 truncate">{displayName(m)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function KioskIdentityGate({
  pinRequired,
  roster,
  rosterError,
  onRetryRoster,
  onIdentify,
  busy,
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-6 py-10">
      {pinRequired ? (
        <PinPad onIdentify={onIdentify} busy={busy} />
      ) : (
        <NameGrid
          roster={roster}
          rosterError={rosterError}
          onRetryRoster={onRetryRoster}
          onIdentify={onIdentify}
          busy={busy}
        />
      )}
    </div>
  );
}
